import { Row, Table as TableInstance, flexRender } from '@tanstack/react-table';
import { VirtualItem, useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData> {
  table: TableInstance<TData>;
  loading?: boolean;
  skeletonRows?: number;
  onClick?: (row: TData) => void;
  containerClassName?: string;
}

export function DataTable<TData>({
  table,
  loading,
  onClick,
  skeletonRows = 5,
  containerClassName,
}: DataTableProps<TData>) {
  // The virtualizer will need a reference to the scrollable container element
  const tableContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Table divRef={tableContainerRef} divClassName={containerClassName}>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                style={{ width: `${header.column.getSize()}%` }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <VirtualizedTableBody
        table={table}
        tableContainerRef={tableContainerRef}
        loading={loading}
        skeletonRows={skeletonRows}
        onClick={onClick}
      />
    </Table>
  );
}

interface TableBodyProps<TData> {
  table: TableInstance<TData>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  loading?: boolean;
  skeletonRows?: number;
  onClick?: (row: TData) => void;
}

function VirtualizedTableBody<TData>({
  table,
  tableContainerRef,
  loading,
  skeletonRows,
  onClick,
}: TableBodyProps<TData>) {
  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    // TODO: this is a temporary value, we need to find a better way to estimate the row height
    estimateSize: () => 57, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <TableBody
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: 'relative',
      }}
    >
      {loading ? (
        Array(skeletonRows)
          .fill(0)
          .map((_, index) => (
            <TableRow key={index}>
              {table.getAllColumns().map((column) => {
                const size = column.columnDef.size ?? 100;
                return (
                  <TableCell key={column.id}>
                    {column.columnDef.id === 'actions' ? (
                      <div className="flex gap-2 justify-end">
                        <Skeleton className="h-8 w-8" />
                      </div>
                    ) : (
                      <Skeleton className={`h-6 w-[${size - 40}px]`} />
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
      ) : virtualItems.length ? (
        virtualItems.map((virtualRow: VirtualItem) => {
          const row = rows[virtualRow.index];
          return (
            <TableBodyRow
              key={row.id}
              row={row}
              virtualRow={virtualRow}
              onClick={onClick}
            />
          );
        })
      ) : (
        <TableRow>
          <TableCell
            colSpan={table.getAllColumns().length}
            className="h-24 text-center"
          >
            No results.
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}

interface TableBodyRowProps<TData> {
  row: Row<TData>;
  virtualRow: VirtualItem;
  onClick?: (row: TData) => void;
}

function TableBodyRow<TData>({
  row,
  virtualRow,
  onClick,
}: TableBodyRowProps<TData>) {
  return (
    <TableRow
      key={row.id}
      style={{
        top: `${virtualRow.start}px`,
      }}
      className={cn(
        onClick ? 'cursor-pointer hover:bg-muted' : '',
        'flex absolute w-full',
      )}
      onClick={onClick ? () => onClick(row.original) : undefined}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          key={cell.id}
          style={{ minWidth: `${cell.column.getSize()}%` }}
          className="flex items-center"
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
}
